<template>
  <teleport to="body">
    <div v-if="isOpen" class="command-palette-overlay" @click="close">
      <div class="command-palette b-1 r-4" @click.stop>
        <div class="command-palette-header flex items-center g-3 bb-1">
          <font-awesome-icon icon="search" class="text-muted text-5" />
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            class="command-palette-input"
            placeholder='Type a command or search... (try is:person, is:note, tag:SF, name:"John Smith")'
            @keydown.down.prevent="selectNext"
            @keydown.up.prevent="selectPrevious"
            @keydown.enter.prevent="executeSelected"
            @keydown.esc="close"
          />
          <button
            class="help-btn r-2"
            :class="{ 'is-active': showHelp }"
            @click="showHelp = !showHelp"
          >
            <font-awesome-icon icon="question-circle" />
          </button>
          <button class="close-btn r-2" @click="close">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div v-if="showHelp" class="command-palette-help bb-1">
          <div class="help-title font-semibold">Search Filters</div>
          <div class="help-grid">
            <code>is:person</code><span>Filter by type (person, organization, event, list)</span>
            <code>is:note</code
            ><span>Filter by schema (note, email, …) — falls back when no type matches</span>
            <code>schema:note</code><span>Explicit schema filter (avoids type collisions)</span>
            <code>name:tom</code><span>Search by name or description</span> <code>tag:SF</code
            ><span>Filter by tag</span> <code>name:"John Smith"</code
            ><span>Use quotes for multi-word values</span> <code>is:person tag:SF</code
            ><span>Combine multiple filters</span>
          </div>
          <div class="help-title font-semibold">Keyboard</div>
          <div class="help-grid">
            <kbd>↑↓</kbd><span>Navigate results</span> <kbd>Enter</kbd><span>Open selected</span>
            <kbd>Esc</kbd><span>Close</span>
          </div>
        </div>

        <div v-if="filteredResults.length > 0" class="command-palette-results">
          <template
            v-for="(item, index) in filteredResults"
            :key="`${item.type}-${item.id || item.action}`"
          >
            <div
              v-if="isGroupHeader(index)"
              class="command-palette-group-header text-7 text-muted font-semibold"
            >
              <font-awesome-icon :icon="item.icon" fixed-width class="mr-1" />
              {{ getGroupLabel(item.recordType) }}
            </div>
            <component
              :is="getItemHref(item) ? 'a' : 'div'"
              :href="getItemHref(item) || undefined"
              class="command-palette-item flex items-center g-3"
              :class="{ 'is-selected': selectedIndex === index }"
              @click="handleItemClick($event, item)"
              @mouseenter="selectedIndex = index"
            >
              <div class="item-icon flex items-center justify-center r-3">
                <font-awesome-icon :icon="item.icon" />
              </div>
              <div class="item-content">
                <div class="item-title is-elided font-medium">{{ item.title }}</div>
                <div v-if="item.subtitle" class="item-subtitle is-elided text-7 text-muted">
                  {{ item.subtitle }}
                </div>
              </div>
              <div v-if="item.tags?.length" class="item-tags flex g-1">
                <span
                  v-for="tag in item.tags"
                  :key="tag.name"
                  class="item-tag r-1 text-7"
                  :style="tag.color ? { backgroundColor: tag.color, color: 'white' } : {}"
                >
                  {{ tag.name }}
                </span>
              </div>
              <div v-if="item.badge" class="item-badge r-2 text-7 font-semibold">
                {{ item.badge }}
              </div>
              <div v-if="item.shortcut" class="item-shortcut b-1 r-2 text-7 text-muted">
                {{ item.shortcut }}
              </div>
            </component>
          </template>
        </div>

        <div
          v-else-if="query.length > 0"
          class="command-palette-empty flex flex-col items-center justify-center text-muted"
        >
          <font-awesome-icon icon="search" class="text-2 mb-3" style="opacity: 0.5" />
          <p class="mb-0">{{ emptyMessage }}</p>
        </div>

        <div
          v-else
          class="command-palette-empty flex flex-col items-center justify-center text-muted"
        >
          <font-awesome-icon icon="key" class="text-2 mb-3" style="opacity: 0.5" />
          <p class="mb-0">Type to search or use actions</p>
        </div>
      </div>
    </div>
  </teleport>
  <AddTaskModal :show="showAddTask" @close="showAddTask = false" @created="showAddTask = false" />
</template>

<script setup lang="ts">
import { computed, nextTick, type Ref, ref, watch } from "vue"
import { useRouter } from "vue-router"
import { toast } from "vue-sonner"
import { useQuery } from "@vue/apollo-composable"
import { getRecordIcon, getRecordUrl } from "@/common/records"
import AddTaskModal from "@/components/tasks/AddTaskModal.vue"
import { useChat } from "@/composables/useChat"
import { useCommandPalette } from "@/composables/useCommandPalette"
import { useQuickCreate } from "@/composables/useQuickCreate"
import { graphql } from "@/gql"
import { client } from "@/init/apollo"
import { getAuger } from "@/init/auger"
import { getAllSchemas, getSchemaName, useSchemasStore } from "@/stores/schemas"
import { getTypeLabel, isValidTypeName } from "@/stores/types"
import { buildSearchWhere } from "@/utils/commandSearch"
import { parseSearchQuery } from "@/utils/searchParser"

// Types
interface ActionItem {
  action: string
  title: string
  icon: string
  shortcut?: string
  badge?: string
  category: string
}

interface ResultItem {
  type: "action" | "record"
  action?: string
  id?: number
  recordType?: string
  title: string
  subtitle?: string
  icon: string
  slug?: string
  shortcut?: string
  badge?: string
  tags?: Array<{ name: string; color?: string | null }>
}

// Define available actions - go- actions derive route name from action
const actions: ActionItem[] = [
  // Navigation - action names match route names for go- actions
  {
    action: "go-home",
    title: "Go to Home",
    icon: "home",
    shortcut: "g h",
    category: "navigation",
  },
  {
    action: "go-calendar",
    title: "Go to Calendar",
    icon: "calendar-alt",
    shortcut: "g c",
    category: "navigation",
  },
  {
    action: "go-members",
    title: "Go to Members",
    icon: "users",
    shortcut: "g m",
    category: "navigation",
  },
  {
    action: "go-financing",
    title: "Go to Financing",
    icon: "dollar-sign",
    shortcut: "g f",
    category: "navigation",
  },
  {
    action: "go-search",
    title: "Go to Search",
    icon: "search",
    shortcut: "g s",
    category: "navigation",
  },
  {
    action: "go-events-overview",
    title: "Go to Events",
    icon: "calendar",
    category: "navigation",
  },
  { action: "go-tags", title: "Go to Tags", icon: "tag", category: "navigation" },
  { action: "go-sources", title: "Go to Sources", icon: "database", category: "navigation" },
  {
    action: "go-pipelines",
    title: "Go to Pipelines",
    icon: "columns",
    shortcut: "g p",
    category: "navigation",
  },
  { action: "go-settings", title: "Go to Settings", icon: "cog", category: "navigation" },

  // Quick Add
  { action: "add-person", title: "Add Person", icon: "user-plus", category: "create" },
  { action: "add-organization", title: "Add Organization", icon: "building", category: "create" },
  { action: "add-event", title: "Add Event", icon: "calendar-alt", category: "create" },
  { action: "add-list", title: "Add List", icon: "list", category: "create" },
  { action: "add-task", title: "Add Task", icon: "plus-circle", category: "create" },

  // AI
  { action: "ai-chat", title: "AI: Open chat", icon: "robot", badge: "AI", category: "ai" },
]

// Record types for add- actions (action name minus "add-" prefix)
const addActionTypes: Record<string, string> = {
  "add-person": "person",
  "add-organization": "organization",
  "add-event": "event",
  "add-list": "list",
}

const chat = useChat({ client })

// AI actions that require being on a record page

// Query to search across all records for command palette
// Uses $where variable so all filtering (type, name, tag, schema_id) happens
// server-side. Schema name → schema_id resolution is done client-side via
// the schemas store before building $where.
const searchRecordsQuery = graphql(`
  query commandPaletteSearch($where: records_bool_exp!) {
    records(
      where: $where
      limit: 20
      order_by: [
        { type_info: { position: asc } }
        { popularity: { score_10t: desc_nulls_last } }
        { name: asc }
      ]
    ) {
      id
      type
      name
      slug
      schema_id
      description
      updated_at
      taggings {
        tag_id
        tag {
          id
          name
          color
        }
      }
    }
  }
`)

const emit = defineEmits<{
  close: []
}>()

const quickCreate = useQuickCreate({ client, toast })
const commandPalette = useCommandPalette()
const { isOpen } = commandPalette

const router = useRouter()
const query = ref("")
const selectedIndex = ref(0)
const showHelp = ref(false)
const showAddTask = ref(false)
const searchInput = ref<HTMLInputElement>()

// Debounce search to avoid firing on every keystroke
let debounceTimer: ReturnType<typeof setTimeout> | null = null
const debouncedQuery = ref("")

const useDebounced = (source: Ref<string>, delay = 200) => {
  watch(source, (val) => {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      debouncedQuery.value = val
    }, delay)
  })
}
useDebounced(query)

const schemasStore = useSchemasStore()

const searchResult = computed(() =>
  buildSearchWhere(debouncedQuery.value, {
    schemas: getAllSchemas(),
    isValidType: isValidTypeName,
  })
)

// Distinguish "no records match" from "no schema named X" once schemas
// have loaded — before then we don't know which it is, so stay generic.
const emptyMessage = computed(() => {
  const unresolved = searchResult.value.unresolvedSchema
  if (unresolved && schemasStore.initialized) {
    return `No schema named "${unresolved}"`
  }
  return "No results found"
})

// Query for records — server-side filtering via Hasura where clause
const { result } = useQuery(
  searchRecordsQuery,
  () => ({ where: searchResult.value.where }),
  () => ({
    enabled: debouncedQuery.value.trim().length > 1,
  })
)

const records = computed(() => result.value?.records ?? [])

// Filter and combine actions and records
const filteredResults = computed((): ResultItem[] => {
  const q = query.value.toLowerCase().trim()

  if (q.length === 0) {
    // Show all actions when no query
    return actions.map((a) => ({ ...a, type: "action" as const }))
  }

  const filters = parseSearchQuery(q)

  // Filter actions (only for non-structured queries)
  const matchedActions = filters.isStructured
    ? []
    : actions
        .filter(
          (a) =>
            a.title.toLowerCase().includes(q) ||
            a.action.toLowerCase().includes(q) ||
            a.shortcut?.toLowerCase().includes(q)
        )
        .map((a) => ({ ...a, type: "action" as const }))

  // Map server-filtered records to result items. `slug` is required for
  // navigation — drop slug-less rows so we don't render dead-click results.
  const matchedRecords: ResultItem[] = records.value
    .filter((r) => r.type !== "search" && r.slug)
    .map((r) => {
      const schemaName = r.schema_id != null ? getSchemaName(r.schema_id) : null
      const typeLine = schemaName ? `${r.type}:${schemaName.toLowerCase()}` : getTypeLabel(r.type)
      return {
        id: r.id,
        type: "record" as const,
        recordType: r.type,
        title: r.name,
        subtitle: r.description?.substring(0, 100) || typeLine,
        icon: getRecordIcon(r),
        slug: r.slug,
        tags: r.taggings?.slice(0, 3).map((t) => ({
          name: t.tag.name,
          color: t.tag.color,
        })),
      }
    })

  // Combine and limit results
  return [...matchedActions, ...matchedRecords].slice(0, 20)
})

// Group header helpers
const isGroupHeader = (index: number) => {
  const item = filteredResults.value[index]
  if (!item || item.type !== "record") return false
  const prev = filteredResults.value[index - 1]
  return !prev || prev.type !== "record" || prev.recordType !== item.recordType
}

const getGroupLabel = (type?: string) => (type ? getTypeLabel(type, 2) : "")

// Keyboard navigation
const selectNext = () => {
  selectedIndex.value = (selectedIndex.value + 1) % filteredResults.value.length
}

const selectPrevious = () => {
  selectedIndex.value =
    selectedIndex.value === 0 ? filteredResults.value.length - 1 : selectedIndex.value - 1
}

const executeSelected = () => {
  const item = filteredResults.value[selectedIndex.value]
  if (item) {
    execute(item)
  }
}

// Resolve a record item's href so it renders as a real anchor — keeps
// cmd/ctrl/middle-click and "Open in new tab" working with native browser
// behavior. `getRecordUrl` returns a route descriptor; resolve it to a real
// URL string so `<a :href>` is meaningful.
const getItemHref = (item: ResultItem): string | null => {
  if (item.type !== "record" || !item.recordType || !item.slug) return null
  return router.resolve(getRecordUrl({ type: item.recordType, slug: item.slug })).href
}

const isModifiedClick = (event: MouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1

const handleItemClick = (event: MouseEvent, item: ResultItem) => {
  const isAnchor = getItemHref(item) !== null
  // Anchor items: let the browser handle modified clicks (new tab, new window).
  if (isAnchor && isModifiedClick(event)) return
  // Only suppress default navigation when there is one to suppress — calling
  // preventDefault on a <div> is harmless today but would silently block
  // future anchor upgrades.
  if (isAnchor) event.preventDefault()
  execute(item)
}

// Execute action or navigate to record
const execute = (item: ResultItem) => {
  if (item.type === "record" && item.recordType && item.slug) {
    router.push(getRecordUrl({ type: item.recordType, slug: item.slug }))
    close()
  } else if (item.type === "action" && item.action) {
    executeAction(item.action)
  }
}

const executeAction = (action: string) => {
  // Handle go- actions by deriving route name from action
  if (action.startsWith("go-")) {
    const routeName = action.slice(3) // Remove "go-" prefix
    router.push({ name: routeName })
    close()
  }
  // Handle add-task: open the task modal
  else if (action === "add-task") {
    showAddTask.value = true
    close()
  }
  // Handle add-person / add-organization: open QuickCreateModal
  else if (action === "add-person") {
    quickCreate.open({ type: "person" })
    close()
  } else if (action === "add-organization") {
    quickCreate.open({ type: "organization" })
    close()
  }
  // Handle other add- actions: create record directly, then navigate
  else if (action.startsWith("add-")) {
    const recordType = addActionTypes[action]
    if (!recordType) return close()

    getAuger(client)
      .records.create({ type: recordType, name: `New ${getTypeLabel(recordType)}` })
      .then((record) => {
        router.push(getRecordUrl({ type: record.type, slug: record.slug }))
        toast.success(`Created ${getTypeLabel(recordType)}: ${record.name}`)
      })
      .catch(() => {
        toast.error(`Failed to create ${getTypeLabel(recordType)}`)
      })
    close()
  }
  // Handle AI actions
  else if (action.startsWith("ai-")) {
    chat.open()
    close()
  } else {
    close()
  }
}

// React to isOpen flipping on/off: reset state, warm schemas, focus on open.
watch(isOpen, (open) => {
  if (open) {
    query.value = ""
    selectedIndex.value = 0
    // Warm schemas store so resolveSchemaIds (sync) can map is:<schema> /
    // schema:<name> to ids on the first keystroke. fetch() is idempotent.
    schemasStore.fetch().catch((err) => {
      console.error("CommandPalette: schemas fetch failed", err)
    })
    nextTick(() => {
      searchInput.value?.focus()
    })
  } else {
    query.value = ""
    selectedIndex.value = 0
    showHelp.value = false
    emit("close")
  }
})

const close = commandPalette.close

// Reset selection when query changes
watch(query, () => {
  selectedIndex.value = 0
})
</script>

<style>
.command-palette-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--auger-overlay);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;

  .command-palette {
    background: var(--auger-bg);
    width: 90%;
    max-width: 600px;
    max-height: 70vh;
    display: flex;
    flex-direction: column;
    box-shadow: var(--auger-shadow-overlay);
    overflow: hidden;
  }

  .command-palette-header {
    padding: 1rem;
  }

  .command-palette-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 1rem;
    color: var(--auger-text);
    font-family: inherit;

    &::placeholder {
      color: var(--auger-text-weak);
    }
  }

  .help-btn,
  .close-btn {
    background: none;
    border: none;
    color: var(--auger-text-weak);
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    transition: all 150ms;

    &:hover,
    &.is-active {
      background: var(--auger-bg);
      color: var(--auger-text);
    }
  }

  .command-palette-help {
    padding: 0.75rem 1rem;
    background: var(--auger-bg);

    .help-title {
      font-size: 0.65rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--auger-text-weak);
      margin-bottom: 0.35rem;

      &:not(:first-child) {
        margin-top: 0.5rem;
      }
    }

    .help-grid {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.3rem 0.75rem;
      font-size: 0.8rem;
      align-items: baseline;
    }

    code,
    kbd {
      font-family: var(--auger-font-mono);
      font-size: 0.75rem;
      padding: 0.1rem 0.35rem;
      background: var(--auger-bg);
      border-radius: 3px;
      white-space: nowrap;
    }
  }

  .command-palette-results {
    overflow-y: auto;
    max-height: calc(70vh - 80px);
  }

  .command-palette-item {
    padding: 0.75rem 1rem;
    cursor: pointer;
    transition: background 100ms;
    border-bottom: 1px solid var(--auger-border-light);
    color: inherit;
    text-decoration: none;

    &:last-child {
      border-bottom: none;
    }

    &:hover,
    &.is-selected {
      background: var(--auger-bg);
    }

    &.is-selected {
      border-left: 3px solid var(--auger-primary);
    }
  }

  .item-icon {
    width: 2rem;
    height: 2rem;
    background: var(--auger-bg);
    color: var(--auger-primary);
    font-size: 1rem;
  }

  .item-content {
    flex: 1;
    min-width: 0;
  }

  .item-badge {
    padding: 0.25rem 0.5rem;
    background: var(--auger-primary);
    color: white;
    text-transform: uppercase;
  }

  .item-shortcut {
    padding: 0.25rem 0.5rem;
    background: var(--auger-bg);
    font-family: var(--auger-font-mono);
  }

  .command-palette-group-header {
    padding: 0.5rem 1rem 0.25rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-top: 1px solid var(--auger-border-light);

    &:first-child {
      border-top: none;
    }
  }

  .item-tags {
    flex-shrink: 0;
  }

  .item-tag {
    padding: 0.1rem 0.35rem;
    background: var(--auger-bg);
    font-size: 0.65rem;
    white-space: nowrap;
  }

  .command-palette-empty {
    padding: 3rem 1rem;
  }
}
</style>
